import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  Grid3x3,
  Plus,
  Trash2,
  Upload,
  Download,
  Loader2,
  X,
  Music,
  Sparkles,
  Palette,
  Save,
  Copy,
  ClipboardPaste,
  GripVertical,
  BarChart3,
  Wand2,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Check,
} from "lucide-react";

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PALETTE = [
  "#8B5CF6", "#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#EC4899",
  "#14B8A6", "#F97316", "#84CC16", "#06B6D4", "#A855F7", "#F43F5E",
];

const fmtHM = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export default function Programs() {
  const [tab, setTab] = useState("grid"); // "grid" | "categories"
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [grid, setGrid] = useState({ slot_minutes: 30, slots_per_day: 48, cells: [] });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [newCat, setNewCat] = useState({ open: false, name: "", color: PALETTE[0], description: "" });
  const [editCat, setEditCat] = useState(null);
  const [copyBuffer, setCopyBuffer] = useState(null); // {kind:"row"|"col", data:[]}
  const [coverage, setCoverage] = useState({ window_min: 0, categories: {} });
  const importInputRef = useRef(null);

  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.id] = c; });
    return m;
  }, [categories]);

  const cellsByKey = useMemo(() => {
    const m = {};
    grid.cells.forEach((c) => {
      const t = (c.start_time || "").substring(0, 5); // "HH:MM"
      m[`${c.day_of_week}:${t}`] = c;
    });
    return m;
  }, [grid.cells]);

  const timeSlots = useMemo(() => {
    const slots = [];
    const step = grid.slot_minutes || 30;
    for (let m = 0; m < 24 * 60; m += step) slots.push(m);
    return slots;
  }, [grid.slot_minutes]);

  // ---------- data loaders ----------
  const loadChannels = async () => {
    try {
      const r = await api.get("/channels");
      setChannels(r.data);
      if (r.data.length && !channelId) setChannelId(r.data[0].id);
    } catch { /* noop */ }
  };
  const loadCategories = async () => {
    try {
      const r = await api.get("/categories");
      setCategories(r.data);
    } catch { toast.error("Could not load categories"); }
  };
  const loadGrid = async () => {
    if (!channelId) return;
    setLoading(true);
    try {
      const r = await api.get(`/programming-grid/${channelId}`);
      setGrid(r.data);
    } catch { toast.error("Could not load programming grid"); }
    setLoading(false);
  };
  const loadCoverage = async () => {
    if (!channelId) return;
    try {
      const r = await api.get(`/programming-grid/${channelId}/category-coverage`);
      setCoverage(r.data);
    } catch { /* noop */ }
  };

  useEffect(() => { loadChannels(); loadCategories(); }, []);
  useEffect(() => { if (channelId) { loadGrid(); loadCoverage(); } }, [channelId]);

  // Category ids whose on-channel content is shorter than the no-repeat window
  // (repeats unavoidable). window_min === 0 means the rule is off → no badges.
  const shortCatIds = useMemo(() => {
    const s = new Set();
    if (!coverage.window_min) return s;
    Object.entries(coverage.categories || {}).forEach(([id, v]) => {
      if (v && v.covers_window === false) s.add(id);
    });
    return s;
  }, [coverage]);

  // Coverage at a glance: of the categories actually scheduled in this
  // channel's grid, how many can't fill the no-repeat window.
  const coverageSummary = useMemo(() => {
    const scheduled = new Set((grid.cells || []).map((c) => c.category_id).filter(Boolean));
    let short = 0;
    scheduled.forEach((id) => { if (shortCatIds.has(id)) short += 1; });
    return { scheduled: scheduled.size, short, windowMin: coverage.window_min || 0 };
  }, [grid.cells, shortCatIds, coverage.window_min]);

  // ---------- category CRUD ----------
  const createCategory = async () => {
    if (!newCat.name.trim()) return;
    try {
      await api.post("/categories", {
        name: newCat.name.trim(),
        color: newCat.color,
        description: newCat.description || undefined,
      });
      toast.success("Category created");
      setNewCat({ open: false, name: "", color: PALETTE[0], description: "" });
      loadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const saveCategory = async () => {
    if (!editCat) return;
    try {
      await api.patch(`/categories/${editCat.id}`, {
        name: editCat.name,
        color: editCat.color,
        description: editCat.description,
        rotation_rule: editCat.rotation_rule,
        no_repeat_minutes: editCat.no_repeat_minutes,
        no_repeat_artist: editCat.no_repeat_artist !== false,
        channel_ids: editCat.channel_ids || [],
      });
      toast.success("Category updated");
      setEditCat(null);
      loadCategories();
      loadCoverage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category? Cells using it will be cleared.")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Deleted");
      loadCategories();
      loadGrid();
    } catch { toast.error("Delete failed"); }
  };

  const autoColorCategories = async () => {
    if (!window.confirm("Give every category a unique color? This overwrites current colors and refreshes the grid pills.")) return;
    try {
      const r = await api.post("/categories/auto-color");
      toast.success(`Recoloured ${r.data.updated} categories`);
      loadCategories();
      loadGrid();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-color failed");
    }
  };

  // ---------- Grid publish → live engine ----------
  const [publishInfo, setPublishInfo] = useState(null); // { published_at, slot_count, short_seconds, plan? }
  const [publishing, setPublishing] = useState(false);
  const [trending, setTrending] = useState(null); // { weeks:[], categories:[] }
  const loadTodayPlan = async () => {
    if (!channelId) return;
    try {
      const r = await api.get(`/programming-grid/${channelId}/today`);
      setPublishInfo(r.data);
    } catch { /* noop */ }
  };
  const loadTrending = async () => {
    if (!channelId) return;
    try {
      const r = await api.get(`/programming-grid/${channelId}/trending?weeks=4`);
      setTrending(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadTodayPlan(); loadTrending(); }, [channelId]);

  // ---------- Silence alert check ----------
  const [silenceCheck, setSilenceCheck] = useState(null);
  const [silenceBusy, setSilenceBusy] = useState(false);
  const runSilenceCheck = async (forceSlack = false) => {
    if (!channelId) return;
    setSilenceBusy(true);
    try {
      const r = await api.post(`/alerts/silence-check/${channelId}${forceSlack ? "?force_slack=true" : ""}`);
      setSilenceCheck(r.data);
      if (r.data.slack_posted) {
        toast.success("Slack alert posted");
      } else if (r.data.slack_configured) {
        toast.success(r.data.over_threshold
          ? `Silence ${r.data.silence_hours}h — Slack alert sent`
          : `Silence ${r.data.silence_hours}h — below ${r.data.threshold_hours}h threshold, no alert`);
      } else {
        toast(`Silence ${r.data.silence_hours}h · Set SLACK_WEBHOOK_URL to enable pings`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silence check failed");
    }
    setSilenceBusy(false);
  };

  const publishToday = async () => {
    if (!channelId) return;
    setPublishing(true);
    try {
      const r = await api.post(`/programming-grid/${channelId}/publish`, {});
      toast.success(`Published ${r.data.slot_count} slots for ${r.data.date}`);
      loadTodayPlan();
      loadTrending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
    setPublishing(false);
  };

  // ---------- cell edit ----------
  const setCell = async (dow, hm, categoryId) => {
    const key = `${dow}:${hm}`;
    // Optimistic UI update
    setGrid((g) => {
      const cells = g.cells.filter((c) => !(c.day_of_week === dow && (c.start_time || "").substring(0, 5) === hm));
      if (categoryId) {
        const cat = catById[categoryId];
        cells.push({
          day_of_week: dow,
          start_time: `${hm}:00`,
          duration_sec: (g.slot_minutes || 30) * 60,
          category_id: categoryId,
          title: cat?.name || "",
          color: cat?.color || "#8B5CF6",
          id: `optimistic-${key}`,
        });
      }
      return { ...g, cells };
    });
    try {
      await api.post(`/programming-grid/${channelId}/cell`, {
        day_of_week: dow,
        start_time: hm,
        category_id: categoryId || null,
      });
    } catch (e) {
      toast.error("Cell save failed");
      loadGrid();
    }
  };

  // ---------- Excel import ----------
  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !channelId) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/programming-grid/${channelId}/import-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${r.data.categories_created} new categories, ${r.data.cells_written} cells (${r.data.slot_minutes}-min slots)`);
      loadCategories();
      loadGrid();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setImporting(false);
    if (importInputRef.current) importInputRef.current.value = "";
  };

  // ---------- Excel export ----------
  const handleExport = async () => {
    if (!channelId) return;
    setExporting(true);
    try {
      const r = await api.get(`/programming-grid/${channelId}/export`, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : "programming-grid.xlsx";
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Programming grid exported");
    } catch {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  // ---------- clipboard helpers ----------
  const copyRow = (mins) => {
    const hm = fmtHM(mins);
    const row = DAYS.map((_, dow) => cellsByKey[`${dow}:${hm}`]?.category_id || null);
    setCopyBuffer({ kind: "row", data: row });
    toast.success("Row copied — paste with the ⎘ button on any other row");
  };
  const pasteRow = async (mins) => {
    if (!copyBuffer || copyBuffer.kind !== "row") return;
    const hm = fmtHM(mins);
    for (let dow = 0; dow < 7; dow++) {
      await setCell(dow, hm, copyBuffer.data[dow]);
    }
  };
  const copyDay = (dow) => {
    const col = timeSlots.map((mins) => cellsByKey[`${dow}:${fmtHM(mins)}`]?.category_id || null);
    setCopyBuffer({ kind: "col", data: col });
    toast.success(`${DAYS[dow]} copied — paste with the ⎘ button on any day header`);
  };
  const pasteDay = async (dow) => {
    if (!copyBuffer || copyBuffer.kind !== "col") return;
    for (let i = 0; i < timeSlots.length; i++) {
      await setCell(dow, fmtHM(timeSlots[i]), copyBuffer.data[i]);
    }
  };

  const changeSlotMinutes = async (mins) => {
    if (!channelId) return;
    if (!window.confirm(`Switch to ${mins}-min slots? This resamples the grid — existing cells snap to the new slot boundaries.`)) return;
    try {
      await api.patch(`/channels/${channelId}`, { grid_slot_minutes: Number(mins) });
      toast.success("Slot size updated");
      loadGrid();
    } catch { toast.error("Could not update slot size"); }
  };

  // ---------- drag & drop ----------
  const [activeDrag, setActiveDrag] = useState(null); // { type, catId, dow, hm, category }
  const [preview, setPreview] = useState(null); // { categoryId, category, asset }
  const previewTimerRef = useRef(null);
  const sensors = useSensors(
    // Require a small movement so click-to-pick still works on the pill.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragStart = (event) => {
    const d = event.active.data.current || {};
    if (d.type === "category") {
      setActiveDrag({ type: "category", category: d.category });
    } else if (d.type === "slot") {
      setActiveDrag({ type: "slot", category: d.category, from: { dow: d.dow, hm: d.hm } });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over) return;
    const src = active.data.current || {};
    const dst = over.data.current || {};
    if (dst.type !== "cell") return;
    const targetDow = dst.dow;
    const targetHm = dst.hm;
    const targetKey = `${targetDow}:${targetHm}`;
    const wasOccupied = !!cellsByKey[targetKey];

    if (src.type === "category") {
      await setCell(targetDow, targetHm, src.category.id);
      toast.success(wasOccupied
        ? `Replaced with ${src.category.name}`
        : `${src.category.name} added at ${targetHm}`);
      return;
    }
    if (src.type === "slot") {
      // Same-cell drop → no-op
      if (src.dow === targetDow && src.hm === targetHm) return;
      const sourceCatId = src.category.id;
      const targetCell = cellsByKey[targetKey];
      // Swap semantics if both cells are occupied; move otherwise
      await setCell(targetDow, targetHm, sourceCatId);
      await setCell(src.dow, src.hm, targetCell?.category_id || null);
      toast.success(targetCell
        ? `Swapped ${src.category.name} ↔ ${targetCell.title}`
        : `Moved ${src.category.name} → ${targetHm}`);
    }
  };

  // ---------- hover preview ----------
  const startHoverPreview = (category) => {
    if (!category) return;
    // Debounce so quick sweeps across the grid don't spam requests.
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/categories/${category.id}/preview`);
        setPreview({ categoryId: category.id, category, asset: r.data });
      } catch (e) {
        // 404 → category empty. Show a tiny hint so the user knows to tag songs.
        if (e?.response?.status === 404) {
          setPreview({ categoryId: category.id, category, asset: null });
        }
      }
    }, 500);
  };
  const cancelHoverPreview = () => {
    clearTimeout(previewTimerRef.current);
  };
  const closePreview = () => {
    clearTimeout(previewTimerRef.current);
    setPreview(null);
  };
  useEffect(() => () => clearTimeout(previewTimerRef.current), []);

  return (
    <div className="p-6 space-y-6" data-testid="programs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">§ Programs Module</div>
          <h1 className="font-display font-black text-3xl mt-1 tracking-tight flex items-center gap-2">
            <Grid3x3 className="w-7 h-7 text-fuchsia-400" strokeWidth={1.5} />
            Programming Grid
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Radio-style category clock. Author a Mon–Sun weekly grid — each cell picks a Music Category, and the engine plays eligible tracks at broadcast time.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={channelId || ""} onValueChange={setChannelId}>
            <SelectTrigger className="w-56" data-testid="programs-channel-select">
              <SelectValue placeholder="Select channel" />
            </SelectTrigger>
            <SelectContent>
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`programs-channel-opt-${c.slug}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(grid.slot_minutes)} onValueChange={changeSlotMinutes}>
            <SelectTrigger className="w-32" data-testid="programs-slot-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15-min slots</SelectItem>
              <SelectItem value="30">30-min slots</SelectItem>
              <SelectItem value="60">60-min slots</SelectItem>
            </SelectContent>
          </Select>

          <input
            ref={importInputRef}
            type="file"
            accept=".xls,.xlsx"
            onChange={handleImport}
            className="hidden"
            data-testid="programs-import-input"
          />
          <Button
            variant="outline"
            onClick={() => importInputRef.current?.click()}
            disabled={!channelId || importing}
            data-testid="programs-import-btn"
            title="Import a weekly programming grid from an Excel .xls / .xlsx file"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import Excel grid
          </Button>

          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!channelId || exporting}
            data-testid="programs-export-btn"
            title="Export this channel's weekly grid as an Excel .xlsx file"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export grid
          </Button>

          <Button
            onClick={publishToday}
            disabled={!channelId || publishing}
            data-testid="programs-publish-btn"
            className="tenant-btn"
            title="Materialise today's grid into concrete track picks so the on-air stream picks it up. Runs automatically at UTC midnight."
          >
            {publishing
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Sparkles className="w-4 h-4 mr-2" />}
            Publish today
          </Button>

          <Button
            onClick={() => runSilenceCheck(false)}
            variant="outline"
            disabled={!channelId || silenceBusy}
            data-testid="silence-check-btn"
            className="rounded-full h-10 border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
            title="Preview tomorrow's silence hours (categories with 0 tagged tracks). If >4h and SLACK_WEBHOOK_URL is set, pings Slack."
          >
            {silenceBusy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <BarChart3 className="w-4 h-4 mr-2" />}
            Silence check
          </Button>
        </div>
      </div>

      {silenceCheck && (
        <div
          className={`flex items-center gap-3 px-3 py-2 rounded-md border text-xs ${
            silenceCheck.over_threshold
              ? "border-rose-500/40 bg-rose-500/5"
              : "border-emerald-500/40 bg-emerald-500/5"
          }`}
          data-testid="silence-check-banner"
        >
          <span className={`font-mono uppercase tracking-widest text-[10px] ${silenceCheck.over_threshold ? "text-rose-400" : "text-emerald-400"}`}>
            {silenceCheck.over_threshold ? "⚠ silence risk" : "✓ silence ok"}
          </span>
          <span className="text-muted-foreground">
            {silenceCheck.date}: <span className="text-foreground font-medium">{silenceCheck.silence_hours}h</span> of scheduled silence · {silenceCheck.silent_slot_count}/{silenceCheck.total_slots} slots · threshold {silenceCheck.threshold_hours}h
            {silenceCheck.slack_configured
              ? <> · <span className="text-cyan-400">Slack {silenceCheck.slack_posted ? "posted ✓" : "not posted"}</span></>
              : <> · <span className="text-muted-foreground">no SLACK_WEBHOOK_URL set</span></>}
          </span>
          {silenceCheck.slack_configured && (
            <button
              onClick={() => runSilenceCheck(true)}
              className="ml-auto text-[10px] font-mono uppercase text-fuchsia-400 hover:text-fuchsia-300"
              data-testid="silence-force-slack"
              title="Force-send a Slack test alert regardless of threshold"
            >
              force-send test
            </button>
          )}
        </div>
      )}

      {publishInfo?.published_at && (
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 text-xs"
          data-testid="programs-publish-status"
        >
          <span className="text-emerald-400 font-mono uppercase tracking-widest text-[10px]">On air</span>
          <span className="text-muted-foreground">
            Last published {new Date(publishInfo.published_at).toLocaleString()} · {publishInfo.slot_count || 0} slots
            {publishInfo.short_seconds > 0 && (
              <> · <span className="text-rose-400">{Math.round((publishInfo.short_seconds || 0) / 60)} min unfilled</span></>
            )}
          </span>
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex items-center gap-1 border-b border-border">
        {["grid", "categories"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`programs-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t
                ? "border-fuchsia-500 text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "grid" ? "Weekly Grid" : `Categories (${categories.length})`}
          </button>
        ))}
      </div>

      {tab === "grid" && (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <CategoryDock categories={categories} />
          {coverageSummary.windowMin > 0 && coverageSummary.scheduled > 0 && (
            <div
              data-testid="coverage-summary"
              className={`flex items-center gap-2 text-xs rounded-sm border px-3 py-2 ${
                coverageSummary.short > 0
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                  : "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              }`}
            >
              {coverageSummary.short > 0 ? (
                <>
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>
                    <strong>{coverageSummary.short}</strong> of {coverageSummary.scheduled} scheduled categories can't fill the {coverageSummary.windowMin}-min no-repeat window — repeats are unavoidable in those slots.
                  </span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 shrink-0" />
                  <span>All {coverageSummary.scheduled} scheduled categories can fill the {coverageSummary.windowMin}-min no-repeat window.</span>
                </>
              )}
            </div>
          )}
          <GridAnalytics
            cells={grid.cells}
            slotMinutes={grid.slot_minutes || 30}
            categories={categories}
          />
          <TrendingCategoriesWidget trending={trending} />
          <GridEditor
            grid={grid}
            loading={loading}
            categories={categories}
            catById={catById}
            cellsByKey={cellsByKey}
            timeSlots={timeSlots}
            shortCatIds={shortCatIds}
            coverage={coverage}
            onCellChange={setCell}
            onCopyRow={copyRow}
            onPasteRow={pasteRow}
            onCopyDay={copyDay}
            onPasteDay={pasteDay}
            copyBuffer={copyBuffer}
            onHoverStart={startHoverPreview}
            onHoverEnd={cancelHoverPreview}
          />
          <DragOverlay dropAnimation={null}>
            {activeDrag?.category && (
              <div
                className="px-2 py-1 rounded-sm text-[11px] font-medium text-white shadow-lg pointer-events-none scale-105"
                style={{ backgroundColor: activeDrag.category.color, minWidth: 110 }}
                data-testid="drag-overlay"
              >
                {activeDrag.category.name}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {tab === "categories" && (
        <CategoriesEditor
          categories={categories}
          shortCatIds={shortCatIds}
          coverage={coverage}
          onNew={() => setNewCat((s) => ({ ...s, open: true }))}
          onEdit={setEditCat}
          onDelete={deleteCategory}
          onAutoColor={autoColorCategories}
        />
      )}

      {/* New category dialog */}
      <Dialog open={newCat.open} onOpenChange={(v) => setNewCat((s) => ({ ...s, open: v }))}>
        <DialogContent data-testid="new-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Music className="w-5 h-5 text-fuchsia-400" /> New music category
            </DialogTitle>
            <DialogDescription>
              A category is a smart playlist — tag your assets with it, then slot the category into the grid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={newCat.name}
                onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Afro Zouk, Brand New, Ndombolo Saga"
                data-testid="new-category-name"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewCat((s) => ({ ...s, color: c }))}
                    className={`w-7 h-7 rounded-full border-2 ${newCat.color === c ? "border-foreground scale-110" : "border-transparent"} transition-transform`}
                    style={{ backgroundColor: c }}
                    data-testid={`palette-${c.replace('#', '')}`}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newCat.description}
                onChange={(e) => setNewCat((s) => ({ ...s, description: e.target.value }))}
                placeholder="Short description shown in the grid tooltip"
                data-testid="new-category-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCat({ open: false, name: "", color: PALETTE[0], description: "" })}>Cancel</Button>
            <Button onClick={createCategory} disabled={!newCat.name.trim()} data-testid="new-category-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit category dialog */}
      <Dialog open={!!editCat} onOpenChange={(v) => !v && setEditCat(null)}>
        <DialogContent data-testid="edit-category-dialog">          {editCat && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Edit category · {editCat.name}</DialogTitle>
                <DialogDescription>{editCat.track_count || 0} tracks tagged</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-1.5 flex-wrap mt-1">
                    {PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setEditCat({ ...editCat, color: c })}
                        className={`w-7 h-7 rounded-full border-2 ${editCat.color === c ? "border-foreground scale-110" : "border-transparent"} transition-transform`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Rotation rule</Label>
                  <Select value={editCat.rotation_rule || "random_no_repeat"} onValueChange={(v) => setEditCat({ ...editCat, rotation_rule: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="random_no_repeat">Random · no repeat within window</SelectItem>
                      <SelectItem value="round_robin">Round-robin (fixed order)</SelectItem>
                      <SelectItem value="newest_first">Newest uploads first (heavy rotation)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editCat.rotation_rule === "random_no_repeat" && (
                  <div>
                    <Label>No-repeat window (minutes)</Label>
                    <Input
                      type="number"
                      value={editCat.no_repeat_minutes}
                      onChange={(e) => setEditCat({ ...editCat, no_repeat_minutes: Number(e.target.value) })}
                    />
                  </div>
                )}
                <div className="flex items-start gap-3 rounded-sm border border-border p-3" data-testid="category-artist-rule">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={editCat.no_repeat_artist !== false}
                    data-testid="category-artist-rule-toggle"
                    onClick={() => setEditCat({ ...editCat, no_repeat_artist: editCat.no_repeat_artist === false })}
                    className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${editCat.no_repeat_artist !== false ? "bg-fuchsia-500" : "bg-muted"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editCat.no_repeat_artist !== false ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                  <div>
                    <Label className="cursor-default">Apply artist no-repeat rule</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">On: no two songs by the same artist air within the channel's no-repeat window. Turn off for jingles / station IDs by one artist — the same-song rule still applies.</p>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={editCat.description || ""} onChange={(e) => setEditCat({ ...editCat, description: e.target.value })} />
                </div>
                <div>
                  <Label>Channels (targeting)</Label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Leave empty to play this category on all channels.</p>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-auto" data-testid="category-channels-multiselect">
                    {channels.map((c) => {
                      const sel = (editCat.channel_ids || []).includes(c.id);
                      return (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`category-channel-${c.id}`}
                          onClick={() => {
                            const cur = editCat.channel_ids || [];
                            const next = sel ? cur.filter((x) => x !== c.id) : [...cur, c.id];
                            setEditCat({ ...editCat, channel_ids: next });
                          }}
                          className={`px-2 py-1 rounded-sm text-xs border transition-colors ${sel ? "border-fuchsia-500 bg-fuchsia-500/10 text-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditCat(null)}>Cancel</Button>
                <Button onClick={saveCategory} data-testid="edit-category-save">
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <HoverPreviewPlayer preview={preview} onClose={closePreview} />
    </div>
  );
}

function GridEditor({
  grid, loading, categories, catById, cellsByKey, timeSlots,
  shortCatIds, coverage,
  onCellChange, onCopyRow, onPasteRow, onCopyDay, onPasteDay, copyBuffer,
  onHoverStart, onHoverEnd,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading grid…
      </div>
    );
  }
  return (
    <div className="border border-border rounded-md overflow-auto max-h-[70vh]" data-testid="programming-grid">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-background z-10">
          <tr>
            <th className="w-24 px-2 py-2 text-left font-mono uppercase tracking-widest text-muted-foreground border-b border-r border-border">
              Time
            </th>
            {DAYS.map((d, dow) => (
              <th key={d} className="px-2 py-2 border-b border-r border-border text-center">
                <div className="flex items-center justify-center gap-1.5">
                  <span className="font-display font-bold">{d}</span>
                  <button
                    onClick={() => onCopyDay(dow)}
                    title={`Copy ${d} column`}
                    className="text-muted-foreground hover:text-foreground opacity-60 hover:opacity-100"
                    data-testid={`copy-day-${dow}`}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  {copyBuffer?.kind === "col" && (
                    <button
                      onClick={() => onPasteDay(dow)}
                      title={`Paste to ${d}`}
                      className="text-emerald-400 hover:text-emerald-300"
                      data-testid={`paste-day-${dow}`}
                    >
                      <ClipboardPaste className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {timeSlots.map((mins) => {
            const hm = fmtHM(mins);
            return (
              <tr key={mins} className="hover:bg-muted/30">
                <td className="px-2 py-1 border-r border-b border-border font-mono text-muted-foreground whitespace-nowrap sticky left-0 bg-background">
                  <div className="flex items-center justify-between gap-1">
                    <span>{hm}</span>
                    <div className="flex gap-0.5">
                      <button onClick={() => onCopyRow(mins)} title="Copy row" className="opacity-40 hover:opacity-100" data-testid={`copy-row-${hm.replace(':', '')}`}>
                        <Copy className="w-3 h-3" />
                      </button>
                      {copyBuffer?.kind === "row" && (
                        <button onClick={() => onPasteRow(mins)} title="Paste row" className="text-emerald-400" data-testid={`paste-row-${hm.replace(':', '')}`}>
                          <ClipboardPaste className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </td>
                {DAYS.map((_, dow) => {
                  const cell = cellsByKey[`${dow}:${hm}`];
                  const cat = cell ? catById[cell.category_id] : null;
                  const isShort = !!(cat && shortCatIds?.has(cat.id));
                  const covMin = cat && coverage?.categories?.[cat.id]
                    ? Math.round((coverage.categories[cat.id].content_sec || 0) / 60)
                    : 0;
                  return (
                    <td key={dow} className="border-r border-b border-border p-0.5 min-w-[110px]">
                      <DroppableCell
                        dow={dow}
                        hm={hm}
                        category={cat}
                        cellColor={cell?.color}
                        cellTitle={cell?.title}
                        categories={categories}
                        isShort={isShort}
                        shortTip={isShort ? `Repeats unavoidable: "${cat.name}" has only ~${covMin} min of music on this channel, under the ${coverage?.window_min || 0} min no-repeat window.` : ""}
                        onChange={(v) => onCellChange(dow, hm, v || null)}
                        onHoverStart={onHoverStart}
                        onHoverEnd={onHoverEnd}
                        testId={`cell-${dow}-${hm.replace(':', '')}`}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CategoryDock({ categories }) {
  if (!categories.length) return null;
  return (
    <div
      className="border border-dashed border-fuchsia-500/40 bg-fuchsia-500/5 rounded-md p-3 mb-3"
      data-testid="category-dock"
    >
      <div className="flex items-center gap-2 mb-2 text-[10px] font-mono uppercase tracking-widest text-fuchsia-400">
        <GripVertical className="w-3 h-3" />
        Drag any category onto a slot below · drop replaces the current cell
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {categories.map((c) => (
          <DraggableCategoryChip key={c.id} category={c} />
        ))}
      </div>
    </div>
  );
}

function DraggableCategoryChip({ category }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `cat-${category.id}`,
    data: { type: "category", category },
  });
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`px-2 py-1 rounded-sm text-[11px] font-medium text-white cursor-grab active:cursor-grabbing transition-opacity ${
        isDragging ? "opacity-30" : "hover:brightness-110"
      }`}
      style={{ backgroundColor: category.color }}
      data-testid={`dock-cat-${category.id}`}
      title={`Drag ${category.name} onto a slot · ${category.track_count || 0} tracks`}
    >
      {category.name}
      {category.track_count > 0 && (
        <span className="ml-1.5 text-white/70 text-[9px] font-mono">{category.track_count}</span>
      )}
    </button>
  );
}

function DroppableCell({ dow, hm, category, cellColor, cellTitle, categories, isShort, shortTip, onChange, onHoverStart, onHoverEnd, testId }) {
  const hasCategory = !!category;
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${dow}-${hm}`,
    data: { type: "cell", dow, hm },
  });
  // NOTE: `min-h-[28px] block` forces empty cells to a full-row-sized drop
  // target — otherwise the "—" placeholder has ~10px height, cursor near a
  // row boundary lands inside the next row's bigger rect and dnd-kit
  // resolves to the wrong row.
  return (
    <div
      ref={setDropRef}
      className={`relative block w-full min-h-[28px] transition-shadow ${
        isOver ? "ring-2 ring-fuchsia-400 ring-offset-1 ring-offset-background rounded-sm shadow-lg shadow-fuchsia-500/30" : ""
      }`}
      data-testid={`drop-${dow}-${hm.replace(':', '')}`}
      onMouseEnter={() => hasCategory && onHoverStart?.(category)}
      onMouseLeave={() => onHoverEnd?.()}
    >
      {isShort && (
        <span
          className="absolute -top-1 -right-1 z-10 text-amber-500 bg-background rounded-full"
          title={shortTip}
          data-testid={`cell-shortwindow-${dow}-${hm.replace(':', '')}`}
        >
          <AlertTriangle className="w-3.5 h-3.5" fill="currentColor" fillOpacity={0.15} />
        </span>
      )}
      {hasCategory ? (
        <DraggableSlot
          dow={dow}
          hm={hm}
          category={category}
          categories={categories}
          onChange={onChange}
          testId={testId}
        />
      ) : (
        <CategoryCellPicker
          value=""
          categories={categories}
          color={cellColor}
          label={cellTitle || ""}
          onChange={onChange}
          testId={testId}
        />
      )}
    </div>
  );
}

function DraggableSlot({ dow, hm, category, categories, onChange, testId }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `slot-${dow}-${hm}`,
    data: { type: "slot", category, dow, hm },
  });
  // Slots keep their click-to-repick popover — drag threshold (6px) prevents
  // click misfires when the user actually wants to open the picker.
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`transition-opacity ${isDragging ? "opacity-30" : ""} cursor-grab active:cursor-grabbing`}
    >
      <CategoryCellPicker
        value={category.id}
        categories={categories}
        color={category.color}
        label={category.name}
        onChange={onChange}
        testId={testId}
      />
    </div>
  );
}

function CategoryCellPicker({ value, categories, color, label, onChange, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full text-left px-1.5 py-1 rounded-sm text-[11px] font-medium leading-tight truncate transition-colors ${
          value ? "text-white" : "text-muted-foreground hover:bg-muted"
        }`}
        style={value ? { backgroundColor: color } : {}}
        data-testid={testId}
        title={label}
      >
        {label || "—"}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 top-full left-0 mt-1 w-52 max-h-72 overflow-auto bg-popover border border-border rounded-md shadow-lg p-1">
            <button
              className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded-sm"
              onClick={() => { onChange(""); setOpen(false); }}
              data-testid={`${testId}-clear`}
            >
              Clear cell
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => { onChange(c.id); setOpen(false); }}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded-sm flex items-center gap-2"
                data-testid={`${testId}-opt-${c.id}`}
              >
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: c.color }} />
                <span className="truncate">{c.name}</span>
                {c.track_count > 0 && (
                  <span className="ml-auto text-[9px] text-muted-foreground font-mono">{c.track_count}</span>
                )}
              </button>
            ))}
            {categories.length === 0 && (
              <div className="px-2 py-2 text-xs text-muted-foreground">
                No categories yet — create one first.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CategoriesEditor({ categories, shortCatIds, coverage, onNew, onEdit, onDelete, onAutoColor }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-muted-foreground">
          {categories.length} categor{categories.length === 1 ? "y" : "ies"} · click a card to edit
        </div>
        <div className="flex items-center gap-2">
          {categories.length > 1 && (
            <Button
              onClick={onAutoColor}
              variant="outline"
              className="rounded-full"
              data-testid="auto-color-btn"
              title="Assign a unique HSL color to every category"
            >
              <Wand2 className="w-4 h-4 mr-2" /> Auto-color
            </Button>
          )}
          <Button onClick={onNew} className="rounded-full" data-testid="new-category-btn">
            <Plus className="w-4 h-4 mr-2" /> New category
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {categories.map((c) => (
          <div
            key={c.id}
            className="group relative border border-border rounded-md p-4 hover:border-fuchsia-400 transition-colors bg-card"
            data-testid={`category-card-${c.id}`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
              className="absolute top-2 right-2 text-muted-foreground hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Delete"
              data-testid={`category-delete-${c.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(c)}
              className="w-full text-left"
              data-testid={`category-edit-${c.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                <div className="font-display font-bold truncate">{c.name}</div>
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2 h-8">
                {c.description || <span className="italic opacity-70">No description</span>}
              </div>
              <div className="flex items-center gap-2 mt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <Badge variant="outline" className="text-[9px]">{c.rotation_rule?.replace(/_/g, " ") || "random"}</Badge>
                <span>·</span>
                <span>{c.track_count || 0} tracks</span>
                {c.no_repeat_artist === false && (
                  <>
                    <span>·</span>
                    <span className="text-muted-foreground/80">no artist rule</span>
                  </>
                )}
              </div>
              {shortCatIds?.has(c.id) && (
                <div
                  className="flex items-center gap-1.5 mt-2 text-[10px] text-amber-600 dark:text-amber-500"
                  data-testid={`category-shortwindow-${c.id}`}
                  title={`Only ~${Math.round(((coverage?.categories?.[c.id]?.content_sec) || 0) / 60)} min of music on this channel — under the ${coverage?.window_min || 0} min no-repeat window.`}
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span>Repeats unavoidable (~{Math.round(((coverage?.categories?.[c.id]?.content_sec) || 0) / 60)}m &lt; {coverage?.window_min || 0}m window)</span>
                </div>
              )}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/console/assets?category=${c.id}`); }}
              className="mt-3 w-full text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded-sm border border-border text-muted-foreground hover:text-foreground hover:border-fuchsia-400 transition-colors"
              data-testid={`category-view-songs-${c.id}`}
            >
              View {c.track_count || 0} song{(c.track_count || 0) === 1 ? "" : "s"} →
            </button>
          </div>
        ))}
      </div>
      {categories.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-md">
          <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display font-bold mb-1">No categories yet</div>
          <div className="text-sm text-muted-foreground mb-4">Create a category or import an Excel grid to get started.</div>
          <Button onClick={onNew} data-testid="empty-new-category-btn">
            <Plus className="w-4 h-4 mr-2" /> New category
          </Button>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Analytics widget: hours-per-category-per-week bar chart
// --------------------------------------------------------------------------
function GridAnalytics({ cells, slotMinutes, categories }) {
  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.id] = c; });
    return m;
  }, [categories]);

  const data = useMemo(() => {
    const totals = {};
    (cells || []).forEach((c) => {
      if (!c.category_id) return;
      totals[c.category_id] = (totals[c.category_id] || 0) + (slotMinutes / 60);
    });
    return Object.entries(totals)
      .map(([cid, hours]) => {
        const cat = catById[cid];
        const trackCount = cat?.track_count || 0;
        return {
          id: cid,
          name: cat?.name || (cells.find((x) => x.category_id === cid)?.title) || "?",
          color: cat?.color || "#8B5CF6",
          hours: Number(hours.toFixed(1)),
          trackCount,
          isEmpty: trackCount === 0,
        };
      })
      .sort((a, b) => b.hours - a.hours);
  }, [cells, slotMinutes, catById]);

  const [collapsed, setCollapsed] = useState(false);
  const totalHours = data.reduce((s, d) => s + d.hours, 0);
  const filledPct = ((totalHours / (24 * 7)) * 100).toFixed(0);
  const emptyRows = data.filter((d) => d.isEmpty);
  const emptyHours = emptyRows.reduce((s, d) => s + d.hours, 0);

  if (!data.length) return null;

  return (
    <div className="border border-border rounded-md bg-card mb-3" data-testid="grid-analytics">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-muted/40 transition-colors"
        data-testid="grid-analytics-toggle"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
          <div>
            <div className="font-display font-bold text-sm">Weekly programming mix</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {data.length} genres · {totalHours.toFixed(1)}h of {24 * 7}h ({filledPct}% filled)
            </div>
          </div>
        </div>
        <span className="text-[10px] font-mono uppercase text-muted-foreground">{collapsed ? "expand" : "collapse"}</span>
      </button>
      {!collapsed && (
        <div className="px-4 pb-4 space-y-3">
          {emptyRows.length > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-sm border border-rose-500/40 bg-rose-500/5 text-xs"
              data-testid="grid-analytics-empty-warning"
            >
              <BarChart3 className="w-4 h-4 text-rose-400 shrink-0" />
              <div className="flex-1">
                <span className="text-rose-400 font-medium">
                  {emptyRows.length} categor{emptyRows.length === 1 ? "y" : "ies"} will play silence
                </span>{" "}
                <span className="text-muted-foreground">
                  ({emptyHours.toFixed(1)}h/week: {emptyRows.slice(0, 4).map((r) => r.name).join(", ")}
                  {emptyRows.length > 4 ? `, +${emptyRows.length - 4} more` : ""})
                </span>
              </div>
            </div>
          )}
          <div className="w-full" style={{ height: Math.min(Math.max(data.length * 26, 180), 520) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                <XAxis type="number" hide domain={[0, "dataMax + 2"]} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                  formatter={(v, _n, ctx) => {
                    const d = ctx?.payload;
                    return [
                      `${v} h/week · ${d?.trackCount ?? 0} tracks${d?.isEmpty ? " ⚠ empty" : ""}`,
                      "airtime",
                    ];
                  }}
                />
                <Bar dataKey="hours" radius={[0, 3, 3, 0]}>
                  {data.map((d) => (
                    <Cell
                      key={d.id}
                      fill={d.isEmpty ? "#F43F5E" : d.color}
                      fillOpacity={d.isEmpty ? 0.35 : 1}
                      stroke={d.isEmpty ? "#F43F5E" : "none"}
                      strokeWidth={d.isEmpty ? 1.5 : 0}
                      strokeDasharray={d.isEmpty ? "4 3" : undefined}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Auto-play hover preview — floating bottom-right audio player
// --------------------------------------------------------------------------
function HoverPreviewPlayer({ preview, onClose }) {
  const videoRef = useRef(null);
  const stopTimerRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1 over the 15-s window

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !preview?.asset) return;
    const url = preview.asset.hls_url || preview.asset.origin_url;
    if (!url) return;
    v.muted = false;
    v.volume = 0.6;
    v.currentTime = 0;
    v.src = url;
    v.play().catch(() => { /* autoplay policy — user needs interact once */ });

    // Auto-stop after 15 seconds
    const startedAt = Date.now();
    const tick = () => {
      const pct = Math.min(1, (Date.now() - startedAt) / 15000);
      setProgress(pct);
      if (pct >= 1) {
        try { v.pause(); } catch { /* noop */ }
        onClose();
        return;
      }
      stopTimerRef.current = requestAnimationFrame(tick);
    };
    stopTimerRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(stopTimerRef.current);
      try { v.pause(); v.removeAttribute("src"); v.load(); } catch { /* noop */ }
      setProgress(0);
    };
  }, [preview?.asset?.asset_id]); // re-init only when we get a new asset

  if (!preview) return null;

  return (
    <div
      className="fixed bottom-6 right-6 w-80 bg-card border border-border rounded-md shadow-2xl overflow-hidden z-50 animate-in slide-in-from-bottom-4 fade-in"
      style={{ borderColor: preview.category.color }}
      data-testid="hover-preview-player"
    >
      <div className="h-1 w-full bg-muted overflow-hidden">
        <div
          className="h-full transition-[width] duration-100"
          style={{ width: `${progress * 100}%`, backgroundColor: preview.category.color }}
        />
      </div>
      <div className="p-3 flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
          style={{ backgroundColor: preview.category.color }}
        >
          <Music className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            15s preview · {preview.category.name}
          </div>
          {preview.asset ? (
            <>
              <div className="font-display font-bold text-sm truncate" title={preview.asset.name}>
                {preview.asset.name}
              </div>
              <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                {preview.asset.cdn_ready ? "▶ HLS · Cloudflare edge" : "▶ Origin MP4"}
                {preview.asset.duration_sec ? ` · ${Math.round(preview.asset.duration_sec)}s track` : ""}
              </div>
            </>
          ) : (
            <div className="text-xs text-muted-foreground">
              No tracks tagged yet — assign assets to <span className="text-foreground font-medium">{preview.category.name}</span> from the Asset Library.
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0"
          data-testid="hover-preview-close"
          aria-label="Close preview"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <video
        ref={videoRef}
        playsInline
        preload="none"
        className="hidden"
        data-testid="hover-preview-video"
      />
    </div>
  );
}


// --------------------------------------------------------------------------
// Trending Categories Widget — week-over-week airtime deltas + sparklines
// --------------------------------------------------------------------------
function TrendingCategoriesWidget({ trending }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!trending?.categories?.length) return null;

  // Split by trend for a two-column view: gainers vs decliners/idle
  const gainers = trending.categories.filter((c) => c.trend === "up" || c.trend === "new");
  const decliners = trending.categories.filter((c) => c.trend === "down");
  const flat = trending.categories.filter((c) => c.trend === "flat" || c.trend === "planned");
  const aired = trending.categories.some((c) => c.this_week > 0 || c.last_week > 0);

  const topN = 8;
  return (
    <div className="border border-border rounded-md bg-card mb-3" data-testid="trending-widget">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-muted/40 transition-colors"
        data-testid="trending-toggle"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
          <div>
            <div className="font-display font-bold text-sm">Trending categories</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {aired ? "Week-over-week airtime · " : ""}
              {trending.weeks.length} weeks · {trending.categories.length} categories
            </div>
          </div>
        </div>
        <span className="text-[10px] font-mono uppercase text-muted-foreground">{collapsed ? "expand" : "collapse"}</span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <TrendColumn
            title="Rising"
            icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-500" />}
            rows={gainers.slice(0, topN)}
            emptyLabel={aired ? "No categories rising this week" : "Publish today to start tracking"}
          />
          <TrendColumn
            title="Declining"
            icon={<TrendingDown className="w-3.5 h-3.5 text-rose-500" />}
            rows={decliners.slice(0, topN)}
            emptyLabel="No categories cooling off"
          />
          <TrendColumn
            title="Steady"
            icon={<Minus className="w-3.5 h-3.5 text-muted-foreground" />}
            rows={flat.slice(0, topN)}
            emptyLabel="No steady categories yet"
          />
        </div>
      )}
    </div>
  );
}

function TrendColumn({ title, icon, rows, emptyLabel }) {
  return (
    <div className="border border-border rounded-sm bg-background/40" data-testid={`trending-col-${title.toLowerCase()}`}>
      <div className="px-3 py-2 border-b border-border flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest">
        {icon}
        <span>{title}</span>
        <span className="ml-auto text-muted-foreground">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-4 text-[11px] text-muted-foreground italic">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <TrendRow key={r.id} row={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TrendRow({ row }) {
  const sparkData = (row.weekly_hours || []).map((h, i) => ({ w: i, h }));
  const badge = trendBadge(row);
  return (
    <li
      className="px-3 py-2 flex items-center gap-2 hover:bg-muted/40"
      data-testid={`trend-row-${row.id}`}
      title={`${row.name} · this week ${row.this_week}h · last week ${row.last_week}h · planned ${row.planned_hours}h`}
    >
      <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: row.color }} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium truncate">{row.name}</div>
        <div className="text-[10px] font-mono text-muted-foreground">
          {row.this_week}h this week
          {row.planned_hours > 0 && row.planned_hours !== row.this_week && (
            <> · <span>{row.planned_hours}h planned</span></>
          )}
          {row.track_count === 0 && (
            <> · <span className="text-rose-400">0 tracks</span></>
          )}
        </div>
      </div>
      <div className="w-14 h-6 shrink-0" aria-hidden>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sparkData} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
            <Line type="monotone" dataKey="h" stroke={row.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${badge.classes} shrink-0`} data-testid={`trend-badge-${row.id}`}>
        {badge.label}
      </div>
    </li>
  );
}

function trendBadge(row) {
  if (row.trend === "up") return { label: `▲ ${row.delta_pct}%`, classes: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" };
  if (row.trend === "down") return { label: `▼ ${Math.abs(row.delta_pct)}%`, classes: "text-rose-500 border-rose-500/40 bg-rose-500/10" };
  if (row.trend === "new") return { label: "new", classes: "text-fuchsia-500 border-fuchsia-500/40 bg-fuchsia-500/10" };
  if (row.trend === "planned") return { label: "planned", classes: "text-cyan-500 border-cyan-500/40 bg-cyan-500/10" };
  if (row.trend === "flat") return { label: "flat", classes: "text-muted-foreground border-border" };
  return { label: "idle", classes: "text-muted-foreground border-border" };
}

